import React, { useContext } from "react";
import { isEmpty } from "lodash";

import { UserContext } from "../amplify/authenticator-provider";
import useLang from "./useLang";
import { crmLinkGeneration } from "../components/engagements/activityCenter/opporotunityLayer/crmLinks";
import { buildCrmLinkUtil } from "../helpers/buildCrmLinkUtil";
import { CRM_TYPE_LABELS, CRM_TYPES } from "../constants/crmConstants";
import {
  CRM_OPPORTUNITY_EXTRACTORS,
  CRM_LEAD_ID_EXTRACTORS,
  formatOpportunityId,
  OPPORTUNITY_ID_MAX_LENGTH,
  OPPORTUNITY_ID_VISIBLE_LENGTH,
  extractOpportunityId,
} from "../helpers/opportunityHelper";

const useCrm = () => {
  const [lang] = useLang();
  const { user } = useContext(UserContext);
  const { crmIntegrationType } = user.company;

  /**
   * Mapping of CRM types to their display names in the current language
   * @type {Object.<string, string>}
   */
  const CRM_TYPE_NAMES = {
    [CRM_TYPES.DEALERSOCKET]: lang.dealerSocket,
    [CRM_TYPES.DEALER_TEAM]: lang.dealerteam,
    [CRM_TYPES.SALESFORCE]: lang.salesforce,
    [CRM_TYPES.VIN_SOLUTIONS]: lang.vinSolutions,
    [CRM_TYPES.EL]: lang.ELead,
    [CRM_TYPES.MOMENTUM]: lang.Momentum,
    [CRM_TYPES.ADF]: lang.ADF,
    [CRM_TYPES.TEKION]: CRM_TYPE_LABELS[CRM_TYPES.TEKION],
  };

  /**
   * Short 2-3 letter codes for each CRM, used as the opp-card title
   * prefix (e.g. "DS 6a3938" / "TK 1234abc..."). Full names live in
   * ``CRM_TYPE_NAMES`` and surface via tooltips and the caption row
   * below the title.
   *
   * Kept in sync with ``getCrmLabel`` in helpers/opportunityHelper.js
   * — both functions need the same short code so the formatted label
   * matches whether it's built via getOpprDetails (hook path) or
   * prepareOpportunityList (helper path).
   */
  const CRM_TYPE_SHORT_CODES = {
    [CRM_TYPES.DEALERSOCKET]: "DS",
    [CRM_TYPES.DEALER_TEAM]: "DT",
    [CRM_TYPES.SALESFORCE]: "SF",
    [CRM_TYPES.VIN_SOLUTIONS]: "VIN",
    [CRM_TYPES.EL]: "EL",
    [CRM_TYPES.MOMENTUM]: "MF",
    [CRM_TYPES.TEKION]: "TK",
  };

  /**
   * Gets the opportunity ID based on the current CRM integration type
   * @param {Array} leadOpportunities - Array of lead opportunities
   * @param {string} leadOpportunityId - Specific opportunity ID to find
   * @returns {string|null} The opportunity ID or null if not found
   */
  const getOpportunityId = (leadOpportunities, leadOpportunityId) => {
    if (isEmpty(leadOpportunities)) return null;

    const leadOpportunity = leadOpportunityId
      ? leadOpportunities.find(
          opportunity => opportunity.id.toString() === leadOpportunityId.toString()
        )
      : leadOpportunities[0];

    return (
      CRM_OPPORTUNITY_EXTRACTORS[crmIntegrationType]?.(leadOpportunity) || leadOpportunities[0]?.id
    );
  };

  /**
   * Checks if a lead opportunity exists in the current CRM system
   * @param {Object} leadOpportunity - The lead opportunity object to check
   * @returns {boolean} True if the opportunity exists, false otherwise
   */
  const checkIsOpportunity = leadOpportunity => {
    const hasOpportunityId = CRM_OPPORTUNITY_EXTRACTORS[crmIntegrationType]?.(leadOpportunity);
    return Boolean(hasOpportunityId || leadOpportunity?.id);
  };

  /**
   * Retrieves all CRM-related details for a lead opportunity
   * @param {Object} leadOpportunity - The lead opportunity object
   * @param {Object} selectedLead - The selected lead object containing CRM data
   * @returns {Object} Object containing:
   *   - crmLink: URL to the CRM system
   *   - isOpportunity: Whether it's an opportunity
   *   - leadId: ID of the lead in the CRM
   *   - opportunityId: ID of the opportunity
   *   - linkOpprId: ID used for linking
   *   - salesForceType: Type of Salesforce integration
   */
  const getCrmDetails = (leadOpportunity, selectedLead) => {
    const salesForceType =
      {
        [CRM_TYPES.SALESFORCE]: "usAutoSales",
        [CRM_TYPES.DEALER_TEAM]: "dealerTeam",
      }[crmIntegrationType] || "";

    const isOpportunity = checkIsOpportunity(leadOpportunity);
    const leadId = CRM_LEAD_ID_EXTRACTORS[crmIntegrationType]?.(selectedLead?.crmLeadObj);
    const opprId =
      CRM_OPPORTUNITY_EXTRACTORS[crmIntegrationType]?.(leadOpportunity) || leadOpportunity?.id;

    const linkOpprId =
      crmIntegrationType === CRM_TYPES.VIN_SOLUTIONS
        ? leadOpportunity?.vsLeadOpportunities?.[0]?.vsContactId
        : crmIntegrationType === CRM_TYPES.EL
        ? selectedLead?.fullName || ""
        : opprId;

    const crmLink = crmLinkGeneration(
      crmIntegrationType,
      isOpportunity,
      linkOpprId,
      leadId,
      salesForceType,
      user?.company?.id
    );

    return {
      crmLink,
      isOpportunity,
      leadId,
      opportunityId: opprId,
      linkOpprId,
      salesForceType,
    };
  };

  /**
   * Builds a CRM link and extracts all related CRM details for a given opportunity/lead.
   * Reusable across all components — pass the opportunity item and lead data, get back the full CRM link info.
   *
   * @param {Object} params
   * @param {Object} params.opportunity - The opportunity item (with CRM sub-arrays like dsOpportunityEvents, sfLeadOpportunities, etc.)
   * @param {Object} [params.crmLeadObj] - The lead's CRM object (contains extractedLead data for leadId)
   * @param {Object} [params.selectedLead] - The selected lead object (needed for EL: uses fullName)
   * @param {string} [params.dsLeadId] - Optional DS lead ID for matching specific dsOpportunityEvents
   * @param {boolean} [params.isLeadLink=false] - If true, generates a lead-page link instead of opportunity-page link
   * @returns {Object} { crmLink, isOpportunity, leadId, opportunityId, linkOpprId, salesForceType, hasCrmId }
   */
  const buildCrmLink = ({
    opportunity,
    crmLeadObj,
    selectedLead,
    dsLeadId,
    isLeadLink = false,
  }) => {
    return buildCrmLinkUtil({
      opportunity,
      crmLeadObj,
      selectedLead,
      dsLeadId,
      isLeadLink,
      crmIntegrationType,
      companyId: user?.company?.id,
    });
  };

  const crmTypeName = CRM_TYPE_NAMES[crmIntegrationType] || "EAI";
  // Short code used as the opp-card title prefix (e.g. "DS",
  // "TK"). Full ``crmTypeName`` still drives tooltip/caption rendering.
  const crmTypeShortCode = CRM_TYPE_SHORT_CODES[crmIntegrationType] || "EAI";

  /**
   * Pass the full opportunity object — internally extracts the right CRM id,
   * detects whether it's a real CRM id or an EAI fallback, and returns the
   * correctly labelled string (e.g. "DealerSocket 5678" or "EAI 1234").
   * @param {Object} opportunity - raw opportunity object from GraphQL
   * @param {string} [format] - optional OPPORTUNITY_ID_FORMAT value
   */
  const getOpprLabel = (opportunity, format) => {
    const id = extractOpportunityId(opportunity, crmIntegrationType);
    const hasCrmId = id && String(id) !== String(opportunity?.id);
    return formatOpportunityId(hasCrmId ? crmTypeName : "EAI", id || opportunity?.id, format);
  };

  /**
   * All-in-one helper — pass lead data, get back everything needed to render
   * the ID label and CRM link. Resolves the correct opportunity internally.
   *
   *   label         → display string e.g. "EAI 1234" or "DealerSocket 5678"
   *   crmLink       → external CRM URL (null when no real CRM id)
   *   opportunityId → raw extracted id
   *   hasCrmId      → true when a real CRM id exists (false = EAI fallback)
   *
   * @param {Object} params.lead            - full lead object (has leadOpportunities, crmLeadObj)
   * @param {Object} [params.opportunity]   - pass directly if already resolved; skips lead lookup
   * @param {string} [params.leadOpportunityId] - ID to find specific opportunity in lead.leadOpportunities
   * @param {Object} [params.crmLeadObj]    - override lead.crmLeadObj if needed
   * @param {Object} [params.selectedLead]  - override for EL fullName link
   * @param {string} [params.dsLeadId]      - optional DS lead id override
   * @param {boolean} [params.isLeadLink]   - generate lead-page link instead of opp-page link
   * @param {string} [params.format]        - optional OPPORTUNITY_ID_FORMAT value
   */
  const getOpprDetails = ({
    lead,
    opportunity: opportunityProp,
    leadOpportunityId,
    crmLeadObj,
    selectedLead,
    dsLeadId,
    isLeadLink = false,
    format,
  }) => {
    const resolvedLead = lead || selectedLead;
    const opportunity =
      opportunityProp ||
      (leadOpportunityId
        ? resolvedLead?.leadOpportunities?.find(o => String(o.id) === String(leadOpportunityId))
        : null) ||
      resolvedLead?.leadOpportunities?.[0];

    const { crmLink, opportunityId, ...rest } = buildCrmLinkUtil({
      opportunity,
      crmLeadObj: crmLeadObj || resolvedLead?.crmLeadObj,
      selectedLead: selectedLead || resolvedLead,
      dsLeadId,
      isLeadLink,
      crmIntegrationType,
      companyId: user?.company?.id,
    });

    // BE-resolved link payload wins when present. Same id-selection
    // decision drives BOTH the URL and the label on the server, so
    // the two can't drift (see api/utils/crm_links.py::
    // build_crm_opportunity_link). This closes the Tekion RO-opp
    // bug where the URL was correct but the label fell back to
    // "EAI <internal-id>" because the FE extractor only looked at
    // ``tkLeadId`` and missed the fallthrough to ``tkRepairOrderId``.
    //
    // BE returns ``kind: "EAI"`` when no CRM identifier was
    // available — in that case we still want the "EAI" prefix; only
    // the URL is dropped.
    const beLink = opportunity?.crmLink;
    if (beLink && beLink.id) {
      const beRawId = String(beLink.id || "");
      const beDisplayId =
        beRawId.length > OPPORTUNITY_ID_MAX_LENGTH
          ? beRawId.slice(-OPPORTUNITY_ID_VISIBLE_LENGTH)
          : beRawId;
      const bePrefix = beLink.prefix || "EAI";
      const beHasCrmId = bePrefix !== "EAI";
      return {
        ...rest,
        label: formatOpportunityId(bePrefix, beDisplayId, format),
        fullLabel: formatOpportunityId(beHasCrmId ? crmTypeName : "EAI", beRawId, format),
        crmLink: beLink.url || null,
        opportunityId: beLink.id,
        hasCrmId: beHasCrmId,
      };
    }

    // A CRM id is only "real" when it exists AND differs from the internal opportunity id.
    // extractOpportunityId can return null/undefined when a CRM sub-array exists but its
    // id field is null (e.g. Tekion service-appointment opportunities) — in that case the
    // record is EAI-internal, must show "EAI", and must NOT produce a CRM link.
    const hasCrmId = Boolean(opportunityId) && String(opportunityId) !== String(opportunity?.id);
    // Use the SHORT code for the visible card title (e.g. "DS", "TK")
    // so the prefix never gets truncated to "DealerS..." in narrow
    // layouts. The full name still appears in the hover tooltip via
    // ``fullLabel`` and in the caption row below the title.
    const crmShortName = hasCrmId ? crmTypeShortCode : "EAI";
    const crmFullName = hasCrmId ? crmTypeName : "EAI";
    const rawId = String((hasCrmId ? opportunityId : opportunity?.id) || "");
    // CRM ids longer than 8 chars are truncated to their last 7 chars for display;
    // the full id is exposed via `fullLabel` for a hover tooltip.
    const displayId =
      rawId.length > OPPORTUNITY_ID_MAX_LENGTH
        ? rawId.slice(-OPPORTUNITY_ID_VISIBLE_LENGTH)
        : rawId;
    const label = formatOpportunityId(crmShortName, displayId, format);
    const fullLabel = formatOpportunityId(crmFullName, rawId, format);
    // Never expose a CRM link built from a null/internal id (would be ".../lead/null").
    const safeCrmLink = hasCrmId ? crmLink : null;
    return { ...rest, label, fullLabel, crmLink: safeCrmLink, opportunityId, hasCrmId };
  };

  // Low-level escape hatch: format a raw id you already have.
  // Prefer getOpprDetails / getOpprLabel whenever you have the full object.
  const formatOpprId = (id, format) => formatOpportunityId(crmTypeName, id, format);

  return {
    crmTypeName,
    getOpprDetails,
    getOpprLabel,
    formatOpprId,
    getOpportunityId,
    getCrmDetails,
    buildCrmLink,
  };
};

export default useCrm;
